import Concept from '@/api/objects/Concept';
import { useAppStore } from '@/stores/app';

export type AppLanguageLocale = string;

type LanguageTabItem = {
  key: string;
  shortName?: string;
  order?: number;
};

/** Active app.languages concepts from the Pinia store. */
export const getAppLanguageConcepts = (): Concept[] =>
  useAppStore().concepts?.['app.languages'] ?? [];

/** Sorts language tab items by concept `order` from the database. */
export const sortByLanguageTabOrder = <T extends LanguageTabItem>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const orderA = a.order != null ? +a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order != null ? +b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    const codeA = (a.shortName || a.key || '').toLowerCase();
    const codeB = (b.shortName || b.key || '').toLowerCase();
    return codeA.localeCompare(codeB);
  });

const mapConcepts = (concepts: Concept[], sort = false) => {
  let tmp = Object.keys(concepts).length > 0 ? concepts.map((concept: Concept) => ({
    key: concept.key,
    label: concept.name,
    permission: concept.permission || null,
    value: concept.value || null,
    order: concept.order,
    icon: concept.icon,
    color: concept.color,
    status: concept.status,
    shortName: concept.shortName,
  })) : [];

  if (sort) {
    tmp = tmp.sort((a: any, b: any) => a.key - b.key);
  }

  return tmp;
};

const sortConcepts = (concepts: Concept[], field = 'order') => concepts.sort((a: any, b: any) => (+a[field] - +b[field]));

/** Locale codes from app.languages concepts, ordered by database `order`. */
export const getAppLanguageLocales = (concepts?: Concept[] | null): string[] => {
  const source = concepts ?? getAppLanguageConcepts();
  if (!source?.length) {
    return [];
  }
  return sortConcepts([...source], 'order').map(
    (concept) => (concept.shortName || concept.key || '').toLowerCase(),
  );
};

/** Mapped app.languages tab items from the store (database order). */
export const getAppLanguages = () => mapAppLanguages(getAppLanguageConcepts());

/** Maps app.languages concepts; tab order follows database `order`. */
export const mapAppLanguages = (concepts: Concept[] | undefined | null) => {
  if (!concepts?.length) {
    return [];
  }
  return mapConcepts(sortConcepts([...concepts], 'order'));
};

const findConceptByKey = (conceptName: string, conceptKey: string): Concept | string => {
  const appStore = useAppStore();

  if (!!appStore.concepts && !!appStore.concepts[conceptName]) {
    const conceptFound = appStore.concepts[conceptName].find((typeConcept: Concept) => typeConcept.key === conceptKey);

    if (!!conceptFound) {
      return conceptFound;
    }
  }

  return '';
};

export default {
  mapConcepts,
  mapAppLanguages,
  getAppLanguages,
  getAppLanguageConcepts,
  getAppLanguageLocales,
  sortConcepts,
  sortByLanguageTabOrder,
  findConceptByKey,
};

export {
  mapConcepts,
  sortConcepts,
  findConceptByKey,
};
